import { LayoutType } from "@kalyzee/escaet-ui";
import React, { useContext, useEffect, useRef, useState } from "react";
import { StudentView, PopulatedMeetingData } from "../../../interfaces/populatedData";
import { getMediaParam, MediaKeys, setMediaParam } from "../../../helpers/storage";
import { debounce } from "lodash";
import { appMessage } from "../../../helpers/message";
import { loadSettings, saveSettings } from "../../../helpers/settings";

export interface StudentMeetingOptions {
  disabledVideoSessionIds?: string[];
}

export interface StudentContextState {
  layout: LayoutType;
  setLayout: (type: LayoutType) => void;

  populatedMeetingData: PopulatedMeetingData | undefined;
  setPopulatedMeetingData: (data?: PopulatedMeetingData) => void;
  availableViews: StudentView[] | undefined;
  setAvailableViews: (views?: StudentView[]) => void;
  currentView: StudentView | undefined;
  setCurrentView: (view?: StudentView) => void;

  inputAudioDeviceInfo: MediaDeviceInfo | undefined;
  setInputAudioDeviceInfo: (device?: MediaDeviceInfo) => void;
  inputVideoDeviceInfo: MediaDeviceInfo | undefined;
  setInputVideoDeviceInfo: (device?: MediaDeviceInfo) => void;
  outputAudioDeviceInfo: MediaDeviceInfo | undefined;
  setOutputAudioDeviceInfo: (device?: MediaDeviceInfo) => void;

  selfStreams: { [mediaId: string]: MediaStream } | undefined;
  updateSelfStream: (mediaId: string, stream?: MediaStream) => void;

  volume: number; // 0 to 100 (0% to 100%)
  setVolume: (volume: number) => void;
  setVolumeSliderFocused: (value: boolean) => void;

  audioStreamEnabled: boolean;
  setAudioStreamEnabled: (enabled: boolean) => void;
  videoStreamEnabled: boolean;
  setVideoStreamEnabled: (enabled: boolean) => void;

  getMeetingOptions: (meetingId: string) => StudentMeetingOptions | undefined;
  updateMeetingOptions: (meetingId: string, options: StudentMeetingOptions) => void;
}

const StudentContext = React.createContext<StudentContextState | undefined>(undefined);

export interface StudentContextProviderProps {
  children: React.ReactNode;
}

export const useStudentContext = (): StudentContextState | undefined => useContext(StudentContext);

export const StudentContextProvider = ({ children }: StudentContextProviderProps) => {
  const [layout, setLayout] = useState(() => {
    const settings = loadSettings();
    const layout =settings?.ui?.playersLayout;
    if (!Object.values(LayoutType).includes(layout!)) {
      return LayoutType.Square;
    }
    return layout!;
  });

  const [populatedMeetingData, setPopulatedMeetingData] = useState<PopulatedMeetingData>();
  const [availableViews, setAvailableViews] = useState<StudentView[]>();
  const [currentView, setCurrentView] = useState<StudentView>();

  const [inputAudioDeviceInfo, setInputAudioDeviceInfo] = useState<MediaDeviceInfo>();
  const [inputVideoDeviceInfo, setInputVideoDeviceInfo] = useState<MediaDeviceInfo>();
  const [outputAudioDeviceInfo, setOutputAudioDeviceInfo] = useState<MediaDeviceInfo>();

  const [selfStreams, setSelfStreams] = useState<{ [mediaId: string]: MediaStream }>();

  const [volume, setVolume] = useState(-1); // -1 by default, means not supported
  const volumeSliderFocusedRef = useRef(false);
  const postVolumeRef = useRef({
    setVolume: debounce((v) => {
      appMessage.postVolume(v);
    }, 100, { leading: true, maxWait: 100 }),
  })

  const [audioStreamEnabled, setAudioStreamEnabled] = useState(getMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED) ?? false);
  const [videoStreamEnabled, setVideoStreamEnabled] = useState(getMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED) ?? false);

  const [meetingOptions, setMeetingOptions] = useState<{ [meetingId: string]: StudentMeetingOptions }>({});

  const getMeetingOptions = (meetingId: string) => meetingOptions[meetingId];
  
  const updateMeetingOptions = (meetingId: string, update: StudentMeetingOptions) => {
    setMeetingOptions((prev) => {
      const prevOptions = prev[meetingId] ?? {};
      const updatedOptions = { ...prevOptions, ...update };
      return { ...prev, [meetingId]: updatedOptions };
    });
  }

  const updateSelfStream = (mediaId: string, stream?: MediaStream) => {
    setSelfStreams((prev) => {
      const newStreams = { ...prev };
      if (stream) {
        newStreams[mediaId] = stream;
      } else {
        delete newStreams[mediaId];
      }
      return newStreams;
    });
  }

  useEffect(() => {
    const volumeListener = (volume: number) => {
      if (volumeSliderFocusedRef.current) return; // no update if slider is focused
      setVolume(volume);
    };
    appMessage.addEventListener("volume", volumeListener);
    return () => {
      appMessage.removeEventListener("volume", volumeListener);
    };
  }, []);

  const state: StudentContextState = {
    layout,
    setLayout: (value) => {
      setLayout(value);
      saveSettings({ ui: { playersLayout: value } });
    },

    populatedMeetingData,
    setPopulatedMeetingData,
    availableViews,
    setAvailableViews,
    currentView,
    setCurrentView,

    inputAudioDeviceInfo,
    setInputAudioDeviceInfo,
    inputVideoDeviceInfo,
    setInputVideoDeviceInfo,
    outputAudioDeviceInfo,
    setOutputAudioDeviceInfo,

    selfStreams,
    updateSelfStream,

    volume,
    setVolume: (v) => {
      postVolumeRef.current.setVolume(v);
      setVolume(v);
    },
    setVolumeSliderFocused: (value) => volumeSliderFocusedRef.current = value,

    audioStreamEnabled,
    setAudioStreamEnabled: (enabled) => {
      setMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED, enabled);
      setAudioStreamEnabled(enabled);
    },
    videoStreamEnabled,
    setVideoStreamEnabled: (enabled) => {
      setMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED, enabled);
      setVideoStreamEnabled(enabled);
    },

    getMeetingOptions,
    updateMeetingOptions,
  }

  return (
    <StudentContext.Provider value={state}>
      {children}
    </StudentContext.Provider>
  );
};